import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useEffect, useRef, useState } from 'react';
import { useSubscription } from '@meraki-internal/state';
import { useAsyncEffect } from '../../support/useAsyncEffect';
import { useDevSettings } from '../../utils/useDevSettings';
import { AppPrincipal } from '../../auth/AppPrincipal';
import { OrganizationInsightsState } from '../OrganizationInsightsState';
import {
    buildAgencyDigest,
    buildCaseloadFigures,
    CaseloadReading,
    hasNothingReadable,
    IAgencyCaseloadFigures,
    IUnreadableClient
} from '@innerhive-internal/innerhive-common';
import { buildDemoInsights, DEMO_CLIENT_PRESENTATIONS } from './agencyDemo';
import { DemoClientPhotos } from './DemoClientPhotos';
import { IAvatarIdentity } from '../../avatar/IAvatarIdentity';

/** The figures are the organization's caseload, or the invented one while the demo setting is on. */
export interface IAgencyInsights extends IAgencyCaseloadFigures {
    hasLoaded: boolean;
    /**
     * The organization has clients but could read none of them, so there is nothing to chart and
     * nothing honest to say in place of it. No report draws in this state: every figure on them
     * is a ratio over the readable set, and over an empty one they read as findings about the book
     * rather than as the absence of one.
     */
    nothingReadable: boolean;
    isDemo: boolean;
    /**
     * Clients left out of every figure above, with the reason and enough to name them.
     *
     * A list rather than a count because the two reasons need different responses: a failed read
     * wants trying again, and a permission gap wants somebody to grant access. A reader given only
     * a number can do neither.
     */
    unreadableClients: IUnreadableClient[];
    /** Reads the caseload again, for the retry a failed read offers. */
    retry: () => Promise<void>;
    /** How each client is drawn, with the audited link to a hosted photo. */
    clientAvatar: (careMapId: string) => IAvatarIdentity | undefined;
    /** Each client's reading, the one the map and every list share; absent for a client the digest does not hold. */
    clientReading: (careMapId: string) => CaseloadReading | undefined;
}

/**
 * The photos dealt to the invented clients for this showing of the demo: dealt afresh each time the
 * demo setting turns on and each time its caseload is read again, and held still between renders
 * so the faces do not reshuffle as the reader looks.
 */
const useDemoPhotos = (isDemo: boolean): { photos: string[]; redeal: () => void } => {
    const demoPhotos = useInstance(DemoClientPhotos);
    const deal = () => demoPhotos.deal({ presentations: DEMO_CLIENT_PRESENTATIONS });
    // Dealt before the first render when the page opens with the demo on, so no frame shows faceless
    // clients first.
    const [photos, setPhotos] = useState<string[]>(() => (isDemo ? deal() : []));
    const redeal = () => setPhotos(deal());
    const wasDemo = useRef(isDemo);

    useEffect(() => {
        if (isDemo && !wasDemo.current) {
            redeal();
        }
        wasDemo.current = isDemo;
        // The deal is for this turning on of the demo: nothing else should trigger another.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDemo]);

    return { photos, redeal };
};

/**
 * The caseload roll-up behind the Insights tab, computed by the server.
 *
 * A caseload too young to chart is still drawn from its own figures: the panels that compare across
 * weeks wait on `historyProgress` and say so, and the rest are real at any age. The invented sample
 * appears only under the admin-only demo setting, which skips reading the caseload at all.
 */
export const useAgencyInsights = (orgId: string): IAgencyInsights => {
    const insightsState = useInstance(OrganizationInsightsState);
    useSubscription(() => insightsState);
    useEffect(() => insightsState.watch(), [insightsState]);

    // Checked here as well as at the toggle: the setting stays on the device after the admin who set
    // it signs out, and whoever signs in next must see their own caseload.
    const principal = useInstance(AppPrincipal);
    const isDemo = useDevSettings().showInsightsDemo && principal.isAdmin === true;
    const demo = useDemoPhotos(isDemo);

    useAsyncEffect(async () => {
        if (isDemo) {
            return;
        }
        await insightsState.load(orgId);
    }, [orgId, insightsState, isDemo]);

    const retry = () => insightsState.reload(orgId);

    if (isDemo) {
        return buildDemoInsights({ now: new Date(), retry: async () => demo.redeal(), photos: demo.photos });
    }

    // Empty until the caseload arrives, which the tab covers with its loading state.
    const figures = insightsState.getFigures() ?? buildCaseloadFigures(buildAgencyDigest({ findings: [], clients: [], now: new Date() }));

    return {
        ...figures,
        hasLoaded: insightsState.hasLoaded,
        nothingReadable: hasNothingReadable(figures.digest),
        isDemo,
        unreadableClients: insightsState.getUnreadableClients(),
        retry,
        clientAvatar: insightsState.getClientAvatar,
        clientReading: insightsState.getReading
    };
};
